import { useMemo, useState } from 'react'
import classNames from 'classnames'
import { Form } from 'react-bootstrap'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useUpdateAccountMutation } from '../../../../store/accountUserSettings/accountUserSettings'
import { useGetAllUsersQuery } from '../../../../store/user/user'
import { type Account, type CrossClientEvaluationEmailConfig } from '../../../../models/Account'
import { MultiSelectDropdown, type MultiSelectOption } from '../../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { Button } from '../../../../components/Button/Button'
import { useZocksAuth, useZocksQuota } from '../../../../components/ScopedBlock/hooks'

interface CrossClientEvaluationEmailFormValues {
  enabled: boolean
  notifyTeamMembers: boolean
  teamMemberIds: string[]
}

const DEFAULT_CONFIG: CrossClientEvaluationEmailConfig = {
  enabled: true,
  notifications: { teamMemberIds: [] },
}

const getDefaultFormValues = (config?: CrossClientEvaluationEmailConfig): CrossClientEvaluationEmailFormValues => {
  const resolved = config ?? DEFAULT_CONFIG
  return {
    enabled: resolved.enabled,
    notifyTeamMembers: (resolved.notifications?.teamMemberIds?.length ?? 0) > 0,
    teamMemberIds: resolved.notifications?.teamMemberIds ?? [],
  }
}

export interface CrossClientEvaluationNotificationProps {
  account: Account
}

export const CrossClientEvaluationNotification = ({ account }: CrossClientEvaluationNotificationProps) => {
  const accountId = useAppSelector(selectAccountId)

  const [updateAccount, { isLoading: accountUpdateLoading }] = useUpdateAccountMutation()
  const { data: usersData, isFetching: isUsersLoading } = useGetAllUsersQuery({ accountId, identityTypes: ['idp'] })

  const { hasScope } = useZocksAuth()
  const { hasQuota } = useZocksQuota()
  const hasAccountUpdateScope = hasScope('**', 'account:update', ['account:admin'])
  const crossClientAvailable = hasQuota('ai.crossClientQuery.value')

  const teamMemberOptions: MultiSelectOption[] = useMemo(() => {
    if (!usersData?.data) {
      return []
    }
    return [...usersData.data]
      .sort((u1, u2) => getUserDisplayName(u1).localeCompare(getUserDisplayName(u2)))
      .map((u) => ({ id: u.id, label: getUserDisplayName(u) }))
  }, [usersData])

  const { control, handleSubmit, reset, formState } = useForm<CrossClientEvaluationEmailFormValues>({
    defaultValues: getDefaultFormValues(account.effectiveConfig.crossClientEvaluationEmail),
  })

  const enabled = useWatch({ control, name: 'enabled' })
  const notifyTeamMembers = useWatch({ control, name: 'notifyTeamMembers' })
  const [isOpened, setIsOpened] = useState<boolean>(false)

  if (!crossClientAvailable) {
    return null
  }

  const onSubmit = async (data: CrossClientEvaluationEmailFormValues) => {
    const crossClientEvaluationEmail: CrossClientEvaluationEmailConfig = {
      enabled: data.enabled,
      notifications: {
        teamMemberIds: data.notifyTeamMembers ? data.teamMemberIds : [],
      },
    }
    const result = await updateAccount({
      accountId,
      payload: {
        config: {
          ...account.config,
          crossClientEvaluationEmail,
        },
      },
    })
    if ('data' in result && result.data) {
      reset(getDefaultFormValues(result.data.data.effectiveConfig.crossClientEvaluationEmail))
    }
  }

  const handleCancel = () => {
    reset(getDefaultFormValues(account.effectiveConfig.crossClientEvaluationEmail))
  }

  return (
    <div className='d-flex flex-column gap-4 border-bottom zocks-pb-2 zocks-border-color-gray-400 border-1'>
      <div className='d-flex zocks-gap-0.75 align-items-center justify-content-between'>
        <div className='d-flex flex-column'>
          <h6>Client Query ready</h6>
          <div className='paragraph-small zocks-text-gray-700'>Sent when a Client Query finishes running.</div>
        </div>
        <div className='d-flex flex-row gap-2 align-items-center'>
          <Controller
            name='enabled'
            control={control}
            render={({ field }) => (
              <Form.Switch
                id='cross-client-evaluation-email-enabled'
                checked={field.value}
                onChange={(e) => {
                  field.onChange(e.target.checked)
                  handleSubmit(onSubmit)()
                }}
                disabled={accountUpdateLoading || !hasAccountUpdateScope}
              />
            )}
          />
          <Button onClick={() => setIsOpened((prev) => !prev)} disabled={!enabled}>
            <i className={isOpened ? 'bi bi-chevron-up' : 'bi bi-chevron-down'} />
          </Button>
        </div>
      </div>
      {enabled && isOpened && (
        <>
          <div className='d-flex flex-column zocks-gap-0.75'>
            <div className='paragraph-medium medium'>Who gets notified</div>
            <div className='paragraph-small zocks-text-gray-700'>The user who ran the query always gets an email.</div>
          </div>

          <div className='d-flex flex-column gap-2'>
            <Controller
              name='notifyTeamMembers'
              control={control}
              render={({ field }) => (
                <Form.Check
                  type='checkbox'
                  id='ccq-notify-team-members'
                  label='Notify additional team members'
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  disabled={accountUpdateLoading || isUsersLoading || !hasAccountUpdateScope}
                />
              )}
            />
            {notifyTeamMembers && (
              <div className='ms-4'>
                <Controller
                  name='teamMemberIds'
                  control={control}
                  render={({ field }) => (
                    <MultiSelectDropdown
                      name={field.name}
                      options={teamMemberOptions}
                      initialSelectedOptions={field.value}
                      onSelectedChanged={field.onChange}
                      placeholder='Select team members…'
                      disabled={accountUpdateLoading || isUsersLoading || !hasAccountUpdateScope}
                    />
                  )}
                />
              </div>
            )}
          </div>

          <div className='d-flex zocks-gap-0.625'>
            <Button
              variant='primary'
              onClick={handleSubmit(onSubmit)}
              loading={accountUpdateLoading}
              disabled={accountUpdateLoading || !formState.isDirty || !hasAccountUpdateScope}
            >
              Save changes
            </Button>
            <Button
              variant='borderless'
              className={classNames({ 'zocks-text-primary': !accountUpdateLoading && formState.isDirty })}
              onClick={handleCancel}
              disabled={accountUpdateLoading || !formState.isDirty || !hasAccountUpdateScope}
            >
              Cancel
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
